import { catalog, memberAt, type CatalogMember } from './catalog.js'
import type { AppCodeContext, Completion, CompletionContext, CompletionResult, HoverInfo, SignatureHelp } from './types.js'
import {
  scanLine, wordBefore, openersBefore, calleeBefore, topLevelCommas, firstStringArg, writtenKeys, wordAt, stringAt,
} from './text.js'

/**
 * JavaScript completions for the PDApp SDK — in `.js`/`.ts` files and inside an
 * HTML file's `<script>` elements:
 *
 *   papan.|              → the client's members (`db`, `scripts`, `files`, …)
 *   papan.db.|           → `run`, `runOrThrow`, `subscribe`, `batch`, `snapshot`
 *   PDApp.|              → `create`, …
 *   papan.db.run('|      → the app's op files (`_db/addTask.sql`)
 *   db.batch([{ op: '|   → the same
 *   papan.db.run('_db/addTask.sql', { |  → the op's `:params`
 *   papan.scripts.run('| → the app's `_api/` scripts
 *
 * "papan" means any variable assigned from `PDApp.create()` / `Papan.create()`,
 * plus the names the docs use (`papan`, `pdapp`).
 */

const NAMESPACE_ROOTS = new Set(['PDApp', 'Papan'])
const DEFAULT_CLIENT_NAMES = ['papan', 'pdapp']
const DB_OP_METHODS = new Set(['run', 'runOrThrow', 'subscribe'])
const DB_ARG_METHODS = new Set(['run', 'runOrThrow'])

/** Variables holding a client: `const app = await PDApp.create()`, plus the documented names. */
export function clientNames(doc: string): Set<string> {
  const names = new Set(DEFAULT_CLIENT_NAMES)
  for (const m of doc.matchAll(/\b([A-Za-z_$][\w$]*)\s*=\s*(?:await\s+)?(?:window\.)?(?:PDApp|Papan)\s*\.\s*create\b/g)) names.add(m[1]!)
  return names
}

/** The catalog members a dotted receiver (`papan.db`) resolves to, or null when it isn't the SDK. */
function membersOf(receiver: string[], clients: Set<string>): CatalogMember[] | null {
  const parts = receiver[0] === 'window' ? receiver.slice(1) : receiver
  if (parts.length === 0) return null
  const [root, ...rest] = parts
  const base = NAMESPACE_ROOTS.has(root!) ? catalog.sdk.namespace : clients.has(root!) ? catalog.sdk.client : null
  if (!base) return null
  if (rest.length === 0) return base
  const m = memberAt(base, rest)
  return m?.members ?? null
}

/** The SDK member a dotted callee (`papan.db.run`) names, or null. */
function memberForCallee(callee: string, clients: Set<string>): CatalogMember | null {
  const parts = callee.split('.')
  if (parts.length < 2) return null
  const members = membersOf(parts.slice(0, -1), clients)
  return members?.find((m) => m.name === parts[parts.length - 1]) ?? null
}

export function memberCompletion(m: CatalogMember): Completion {
  const firstSig = m.signature.split('\n')[0]!
  return {
    label: m.name,
    type: m.kind,
    detail: m.kind === 'method' ? firstSig.slice(m.name.length) : m.kind === 'property' ? firstSig.slice(m.name.length + 2) : undefined,
    info: [m.kind === 'namespace' ? '' : m.signature, m.doc].filter(Boolean).join('\n\n'),
  }
}

/** For an HTML file: is the caret inside a `<script>` element (and not one with a `src`)? */
export function inScriptElement(doc: string, pos: number): boolean {
  const before = doc.slice(0, pos).toLowerCase()
  const open = before.lastIndexOf('<script')
  if (open < 0 || before.lastIndexOf('</script') > open) return false
  const tagEnd = before.indexOf('>', open)
  return tagEnd >= 0
}

const isHtml = (fileName: string) => /\.html?$/i.test(fileName)

function fileOptions(paths: string[], detail: string): Completion[] {
  return paths.map((p) => ({ label: p, type: 'file' as const, detail }))
}

export async function jsCompletions(ctx: CompletionContext): Promise<CompletionResult | null> {
  const { doc, pos } = ctx
  if (isHtml(ctx.fileName) && !inScriptElement(doc, pos)) return null
  const line = scanLine(doc, pos, '//')
  if (line.comment) return null
  const clients = clientNames(doc)

  // ── Inside a string: an op path or a script path ─────────────────────
  if (line.stringStart !== null) {
    const before = doc.slice(Math.max(0, line.stringStart - 300), line.stringStart)
    const from = line.stringStart + 1
    const isCallArg0 = /\(\s*$/.test(before)
    const head = before.replace(/\(\s*$/, '')
    const parts = calleeBefore(head, head.length).split('.')
    const callee = parts.join('.')
    if (isCallArg0 && parts.length >= 2 && parts[parts.length - 2] === 'db' && DB_OP_METHODS.has(parts[parts.length - 1]!)) {
      return { from, options: fileOptions(await ctx.app?.opPaths() ?? [], 'op') }
    }
    if (/\bop\s*:\s*$/.test(before)) {
      return { from, options: fileOptions(await ctx.app?.opPaths() ?? [], 'op') }
    }
    if (isCallArg0 && /(^|\.)scripts\.run$/.test(callee)) {
      return { from, options: fileOptions(await ctx.app?.scriptPaths() ?? [], 'script') }
    }
    return null
  }

  const word = wordBefore(doc, pos)
  const wordStart = pos - word.length

  // ── A key in `db.run('_db/x.sql', { … })` → the op's :params ───────────
  const openers = openersBefore(doc, pos, '//')
  const inner = openers[openers.length - 1]
  const call = openers[openers.length - 2]
  if (inner?.ch === '{' && call?.ch === '(') {
    const callee = calleeBefore(doc, call.at)
    const parts = callee.split('.')
    const argsText = doc.slice(call.at + 1, inner.at)
    const objectText = doc.slice(inner.at, pos)
    if (
      parts.length >= 2 && parts[parts.length - 2] === 'db' && DB_ARG_METHODS.has(parts[parts.length - 1]!)
      && topLevelCommas(argsText) === 1 && /(?:^\{|,)\s*[\w$]*$/.test(objectText)
    ) {
      const op = firstStringArg(argsText)
      if (op && ctx.app) {
        const taken = writtenKeys(objectText)
        const params = (await ctx.app.opParams(op)).filter((p) => !taken.has(p))
        return { from: wordStart, options: params.map((p) => ({ label: p, type: 'property', detail: `:${p}`, info: `Bound to \`:${p}\` in ${op}.` })) }
      }
    }
  }

  // ── Member access: papan.db.| ─────────────────────────────────────────
  const beforeWord = doc.slice(Math.max(0, wordStart - 300), wordStart)
  const dot = beforeWord.match(/([A-Za-z_$][\w$]*(?:\s*\??\.\s*[A-Za-z_$][\w$]*)*)\s*\??\.\s*$/)
  if (dot) {
    const members = membersOf(dot[1]!.split(/\s*\??\.\s*/), clients)
    if (!members) return null
    return { from: wordStart, options: members.map(memberCompletion) }
  }

  // ── A bare name: the client variables and the SDK namespace ──────────
  if (!ctx.explicit && word.length < 2) return null
  const options: Completion[] = [...clients].map((n) => ({ label: n, type: 'variable', detail: 'PDApp client' }))
  options.push({ label: 'PDApp', type: 'namespace', detail: 'SDK', info: 'The PDApp SDK. `const papan = await PDApp.create()`.' })
  return { from: wordStart, options }
}

/** The signature of the SDK call the caret is inside (`papan.db.run(|`). */
export function jsSignature(ctx: CompletionContext): SignatureHelp | null {
  const { doc, pos } = ctx
  if (isHtml(ctx.fileName) && !inScriptElement(doc, pos)) return null
  if (scanLine(doc, pos, '//').comment) return null
  const openers = openersBefore(doc, pos, '//')
  for (let i = openers.length - 1; i >= 0; i--) {
    const o = openers[i]!
    if (o.ch !== '(') continue
    const m = memberForCallee(calleeBefore(doc, o.at), clientNames(doc))
    if (m && m.kind === 'method') return { signature: m.signature, doc: m.doc }
    return null
  }
  return null
}

/** Hover text for an app path written in a string — an op file or a script. Shared with Python. */
export async function pathHover(text: string, from: number, to: number, app: AppCodeContext | null): Promise<HoverInfo | null> {
  if (/(^|\/)_db\/.+\.sql$/.test(text)) {
    const params = app ? await app.opParams(text.slice(text.indexOf('_db/'))) : []
    const known = app ? (await app.opPaths()).includes(text.slice(text.indexOf('_db/'))) : true
    return {
      from, to,
      title: `op ${text}`,
      doc: !known ? 'No such op file in this app.'
        : params.length ? `Caller args: ${params.map((p) => `:${p}`).join(', ')} — pass them as \`{ ${params.join(', ')} }\`.`
          : 'Takes no caller args.',
    }
  }
  if (/(^|\/)_api\/.+\.(py|sh)$/.test(text)) {
    const known = app ? (await app.scriptPaths()).includes(text) : true
    return { from, to, title: `script ${text}`, doc: known ? 'Runs in the sandbox; the second argument arrives as `args`.' : 'No such script in this app.' }
  }
  return null
}

/** What hovering a name in JS shows: SDK members (signature + docs), the client, op / script paths. */
export async function jsHover(ctx: CompletionContext): Promise<HoverInfo | null> {
  const { doc, pos } = ctx
  if (isHtml(ctx.fileName) && !inScriptElement(doc, pos)) return null
  const str = stringAt(doc, pos, '//')
  if (str) return pathHover(str.text, str.from, str.to, ctx.app)
  if (scanLine(doc, pos, '//').comment) return null
  const w = wordAt(doc, pos)
  if (!w) return null
  const clients = clientNames(doc)
  const parts = calleeBefore(doc, w.to).split('.')
  if (parts.length === 1) {
    if (NAMESPACE_ROOTS.has(w.word)) return { from: w.from, to: w.to, title: w.word, doc: 'The PDApp SDK. `const papan = await PDApp.create()` connects and returns the client.' }
    if (clients.has(w.word)) return { from: w.from, to: w.to, title: `${w.word}: PapanClient`, doc: 'The PDApp client — `db`, `scripts`, `files`, `events`, `presence`, `whoami()`, `access()`, …' }
    return null
  }
  const m = memberForCallee(parts.join('.'), clients)
  if (!m) return null
  return { from: w.from, to: w.to, title: m.kind === 'namespace' ? `${parts.join('.')}` : m.signature, doc: m.doc }
}
